import { ChangeEvent, KeyboardEvent, useState } from 'react'
import { IconMinus, IconPlus } from '@tabler/icons-react'
import { Product } from '~/types'
import styles from './ProductDashboardQuantitySelector.module.css'

export interface ProductDashboardQuantitySelectorProps {
  product: Product
  quantity: number
  onChange: (quantity: number) => void
}

const MIN_QUANTITY = 1

export const ProductDashboardQuantitySelector = ({
  product,
  quantity,
  onChange,
}: ProductDashboardQuantitySelectorProps) => {
  const maxQuantity = product?.maxQuantity
  // What the field shows while it holds something that cannot be committed
  // yet: cleared, or a value below the minimum such as a leading zero. Snapping
  // it to the minimum on every keystroke would turn a cleared field plus "25"
  // into "125", so it is corrected on blur instead.
  const [draft, setDraft] = useState<string | null>(null)

  const clamp = (value: number) => {
    if (Number.isNaN(value)) {
      return MIN_QUANTITY
    }

    if (value < MIN_QUANTITY) {
      return MIN_QUANTITY
    }

    if (maxQuantity != null && value > maxQuantity) {
      return maxQuantity
    }

    return value
  }

  const update = (next: number) => {
    onChange(clamp(next))
  }

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value

    if (raw !== '' && !/^\d+$/.test(raw)) {
      return
    }

    if (raw === '' || Number(raw) < MIN_QUANTITY) {
      setDraft(raw)

      return
    }

    setDraft(null)
    update(Number(raw))
  }

  const commitDraft = () => {
    if (draft === null) {
      return
    }

    setDraft(null)
    update(Number(draft))
  }

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.currentTarget.blur()
    }
  }

  const canDecrement = quantity > MIN_QUANTITY
  const canIncrement = maxQuantity == null || quantity < maxQuantity

  return (
    <div className={styles.root}>
      <button
        type='button'
        aria-label='Decrease quantity'
        className={styles.controlButton}
        disabled={!canDecrement}
        onClick={() => {
          update(quantity - 1)
        }}>
        <IconMinus size={14} strokeWidth={3} />
      </button>
      <input
        type='text'
        inputMode='numeric'
        pattern='[0-9]*'
        aria-label='Quantity'
        className={styles.input}
        value={draft ?? quantity}
        onChange={handleInputChange}
        onBlur={commitDraft}
        onKeyDown={handleInputKeyDown}
      />
      <button
        type='button'
        aria-label='Increase quantity'
        className={styles.controlButton}
        disabled={!canIncrement}
        onClick={() => {
          update(quantity + 1)
        }}>
        <IconPlus size={14} strokeWidth={3} />
      </button>
    </div>
  )
}
